/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type { useRender } from '@base-ui/react/use-render'
import { Link } from '@tanstack/react-router'
import type { ComponentProps, ReactNode } from 'react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../ui/breadcrumb'
import { Button } from '../../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu'

const LINK_TEXT_CLASS = 'block min-w-0 max-w-full truncate hover:underline hover:text-foreground'

const SEPARATOR_GAP_CLASS = {
  0.5: 'gap-0.5',
  1: 'gap-1',
  1.5: 'gap-1.5',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
} as const

export type PageBreadcrumbGap = keyof typeof SEPARATOR_GAP_CLASS

export type PageBreadcrumbItem = {
  label: ReactNode
  /** Parent segments: link target; defaults to `/` when omitted. Ignored on the last item (current page). */
  to?: string
  /** Polymorphic link (e.g. router `Link`). When set, `href` is not used for that segment. */
  render?: useRender.ComponentProps<'a'>['render']
}

export type PageBreadcrumbProps = Omit<ComponentProps<'nav'>, 'children'> & {
  items: PageBreadcrumbItem[]
  separator?: ReactNode
  separatorGap?: PageBreadcrumbGap
  listClassName?: string
  itemClassName?: string
}

/**
 * Ordered trail: parent segments use `href` or `render`; the last item is the current page.
 * When there are more than three items, leading segments collapse behind an ellipsis control that opens a menu (same behavior as Chakra `PageBreadcrumb`).
 */
export function PageBreadcrumb({
  items,
  separator,
  separatorGap = 0.5,
  listClassName,
  itemClassName,
  className,
  ...rest
}: PageBreadcrumbProps) {
  const { t } = useTranslation()

  if (!items.length) {
    return null
  }

  const parents = items.slice(0, -1)
  const current = items[items.length - 1]
  const collapseLeading = items.length > 3
  const visibleParents = collapseLeading ? parents.slice(-2) : parents
  const collapsedParents = collapseLeading ? parents.slice(0, -2) : []
  const gapCn = SEPARATOR_GAP_CLASS[separatorGap]
  const listCn = cn(gapCn, 'min-w-0 flex-nowrap overflow-hidden text-sm', listClassName)
  const textItemCn = cn('min-w-0', itemClassName)
  const ellipsisItemCn = cn('shrink-0', itemClassName)

  return (
    <Breadcrumb className={cn('min-w-0', className)} {...rest}>
      <BreadcrumbList className={listCn}>
        {collapseLeading ? (
          <Fragment key="ellipsis-trail">
            <BreadcrumbItem className={ellipsisItemCn}>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button aria-label={t('Open breadcrumb menu')} size="icon-sm" type="button" variant="ghost" />
                  }
                >
                  <BreadcrumbEllipsis />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuGroup>
                    {collapsedParents.map((item, index) => {
                      const key = `collapsed-${item.to ?? 'segment'}-${String(index)}`
                      return (
                        <DropdownMenuItem
                          key={key}
                          render={
                            <BreadcrumbLink
                              className={LINK_TEXT_CLASS}
                              {...(item.render != null ? { render: item.render } : { href: item.to ?? '/' })}
                            />
                          }
                        >
                          {item.label}
                        </DropdownMenuItem>
                      )
                    })}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="shrink-0">{separator}</BreadcrumbSeparator>
          </Fragment>
        ) : null}
        {visibleParents.map((item, index) => {
          const key = `${item.to ?? 'segment'}-${String(index)}`
          const link = (
            <BreadcrumbLink
              className={LINK_TEXT_CLASS}
              {...(item.render != null
                ? { render: item.render }
                : { render: <Link to={item.to ?? '/'}>{item.label}</Link> })}
            >
              {item.label}
            </BreadcrumbLink>
          )
          return (
            <Fragment key={key}>
              <BreadcrumbItem className={textItemCn}>{link}</BreadcrumbItem>
              <BreadcrumbSeparator className="shrink-0">{separator}</BreadcrumbSeparator>
            </Fragment>
          )
        })}
        <BreadcrumbItem className={textItemCn}>
          <BreadcrumbPage className={LINK_TEXT_CLASS}>{current?.label}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  )
}
